import React, { useState, useEffect } from 'react';
import './ContactManager.css';
import { getContactSettings, updateContactSettings } from '../../firebase/dataService';

function ContactManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    visible: true,
    title: 'تواصل معنا',
    subtitle: 'نسعد بتواصلكم واستفساراتكم',
    email: 'admin@salman-alkhatep.com',
    twitter: {
      enabled: true,
      url: 'https://x.com/salman_alKhatep',
      handle: '@salman_alKhatep',
      description: 'تابعنا على تويتر للحصول على آخر التحديثات والفوائد اليومية'
    },
    youtube: {
      enabled: true,
      url: 'https://youtube.com/channel/UCJCc6TojI38et5ckeUikRWg?si=SV2ju6GjGLgrKHTB',
      handle: 'قناة سلمان الحران',
      description: 'اشترك في قناتنا على يوتيوب لمشاهدة الدروس والمحاضرات المرئية'
    },
    telegram: {
      enabled: true,
      url: 'https://t.me/salman_alkhatep',
      handle: '@salman_alkhatep',
      description: 'انضم إلى قناتنا على تلجرام للحصول على الفوائد والتصاميم الجديدة'
    },
    responseTime: {
      enabled: true,
      title: 'أوقات الرد',
      description: 'نسعى للرد على جميع الاستفسارات في أقرب وقت ممكن'
    },
    suggestions: {
      enabled: true,
      title: 'اقتراحاتكم',
      description: 'نرحب بجميع الاقتراحات والملاحظات لتطوير المحتوى'
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getContactSettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading contact settings:', error);
      setMessage({ type: 'error', text: 'فشل تحميل الإعدادات' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      await updateContactSettings(settings);
      
      setMessage({ type: 'success', text: '✅ تم حفظ التعديلات بنجاح!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: '❌ فشل حفظ التعديلات' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async () => {
    const newSettings = { ...settings, visible: !settings.visible };
    setSettings(newSettings);
    
    try {
      await updateContactSettings(newSettings);
      setMessage({ 
        type: 'success', 
        text: newSettings.visible ? '✅ تم إظهار صفحة التواصل' : '✅ تم إخفاء صفحة التواصل'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      setSettings(settings);
      setMessage({ type: 'error', text: '❌ فشل تغيير حالة الظهور' });
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner-large"></div>
        <p>جاري تحميل الإعدادات...</p>
      </div>
    );
  }

  return (
    <div className="contact-manager">
      <div className="manager-header">
        <div className="header-actions">
          <button 
            className={`visibility-toggle ${settings.visible ? 'visible' : 'hidden'}`}
            onClick={handleToggleVisibility}
          >
            {settings.visible ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                الصفحة ظاهرة
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
                الصفحة مخفية
              </>
            )}
          </button>
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px', animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6"></line>
                  <line x1="12" y1="18" x2="12" y2="22"></line>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                  <line x1="2" y1="12" x2="6" y2="12"></line>
                  <line x1="18" y1="12" x2="22" y2="12"></line>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                </svg>
                جاري الحفظ...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                حفظ التعديلات
              </>
            )}
          </button>
        </div>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
      </div>

      <div className="settings-sections">
        <div className="settings-section">
          <h3 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            العنوان الرئيسي
          </h3>
          <div className="form-group">
            <label>العنوان</label>
            <input
              type="text"
              value={settings.title}
              onChange={(e) => setSettings({ ...settings, title: e.target.value })}
              placeholder="تواصل معنا"
            />
          </div>
          <div className="form-group">
            <label>العنوان الفرعي</label>
            <input
              type="text"
              value={settings.subtitle}
              onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
              placeholder="نسعد بتواصلكم واستفساراتكم"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            البريد الإلكتروني
          </h3>
          <div className="form-group">
            <label>البريد الإلكتروني</label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              placeholder="admin@salman-alkhatep.com"
            />
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header-with-toggle">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Twitter
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.twitter?.enabled !== false}
                onChange={(e) => setSettings({
                  ...settings,
                  twitter: { ...settings.twitter, enabled: e.target.checked }
                })}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{settings.twitter?.enabled !== false ? 'مفعّل' : 'معطّل'}</span>
            </label>
          </div>
          <div className="form-group">
            <label>رابط الحساب</label>
            <input
              type="url"
              value={settings.twitter.url}
              onChange={(e) => setSettings({
                ...settings,
                twitter: { ...settings.twitter, url: e.target.value }
              })}
              placeholder="https://x.com/username"
            />
          </div>
          <div className="form-group">
            <label>اسم المستخدم</label>
            <input
              type="text"
              value={settings.twitter.handle}
              onChange={(e) => setSettings({
                ...settings,
                twitter: { ...settings.twitter, handle: e.target.value }
              })}
              placeholder="@username"
            />
          </div>
          <div className="form-group">
            <label>الوصف</label>
            <textarea
              value={settings.twitter.description}
              onChange={(e) => setSettings({
                ...settings,
                twitter: { ...settings.twitter, description: e.target.value }
              })}
              placeholder="وصف الحساب"
              rows="2"
            />
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header-with-toggle">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
              </svg>
              YouTube
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.youtube?.enabled !== false}
                onChange={(e) => setSettings({
                  ...settings,
                  youtube: { ...settings.youtube, enabled: e.target.checked }
                })}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{settings.youtube?.enabled !== false ? 'مفعّل' : 'معطّل'}</span>
            </label>
          </div>
          <div className="form-group">
            <label>رابط القناة</label>
            <input
              type="url"
              value={settings.youtube.url}
              onChange={(e) => setSettings({
                ...settings,
                youtube: { ...settings.youtube, url: e.target.value }
              })}
              placeholder="https://youtube.com/channel/..."
            />
          </div>
          <div className="form-group">
            <label>اسم القناة</label>
            <input
              type="text"
              value={settings.youtube.handle}
              onChange={(e) => setSettings({
                ...settings,
                youtube: { ...settings.youtube, handle: e.target.value }
              })}
              placeholder="قناة سلمان الحران"
            />
          </div>
          <div className="form-group">
            <label>الوصف</label>
            <textarea
              value={settings.youtube.description}
              onChange={(e) => setSettings({
                ...settings,
                youtube: { ...settings.youtube, description: e.target.value }
              })}
              placeholder="وصف القناة"
              rows="2"
            />
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header-with-toggle">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              أوقات الرد
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.responseTime?.enabled !== false}
                onChange={(e) => setSettings({
                  ...settings,
                  responseTime: { ...settings.responseTime, enabled: e.target.checked }
                })}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{settings.responseTime?.enabled !== false ? 'مفعّل' : 'معطّل'}</span>
            </label>
          </div>
          <div className="form-group">
            <label>العنوان</label>
            <input
              type="text"
              value={settings.responseTime?.title || 'أوقات الرد'}
              onChange={(e) => setSettings({
                ...settings,
                responseTime: { ...settings.responseTime, title: e.target.value }
              })}
              placeholder="أوقات الرد"
            />
          </div>
          <div className="form-group">
            <label>الوصف</label>
            <textarea
              value={settings.responseTime?.description || ''}
              onChange={(e) => setSettings({
                ...settings,
                responseTime: { ...settings.responseTime, description: e.target.value }
              })}
              placeholder="نسعى للرد على جميع الاستفسارات في أقرب وقت ممكن"
              rows="2"
            />
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header-with-toggle">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
              الاقتراحات
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.suggestions?.enabled !== false}
                onChange={(e) => setSettings({
                  ...settings,
                  suggestions: { ...settings.suggestions, enabled: e.target.checked }
                })}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{settings.suggestions?.enabled !== false ? 'مفعّل' : 'معطّل'}</span>
            </label>
          </div>
          <div className="form-group">
            <label>العنوان</label>
            <input
              type="text"
              value={settings.suggestions?.title || 'اقتراحاتكم'}
              onChange={(e) => setSettings({
                ...settings,
                suggestions: { ...settings.suggestions, title: e.target.value }
              })}
              placeholder="اقتراحاتكم"
            />
          </div>
          <div className="form-group">
            <label>الوصف</label>
            <textarea
              value={settings.suggestions?.description || ''}
              onChange={(e) => setSettings({
                ...settings,
                suggestions: { ...settings.suggestions, description: e.target.value }
              })}
              placeholder="نرحب بجميع الاقتراحات والملاحظات لتطوير المحتوى"
              rows="2"
            />
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header-with-toggle">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              Telegram
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.telegram?.enabled !== false}
                onChange={(e) => setSettings({
                  ...settings,
                  telegram: { ...settings.telegram, enabled: e.target.checked }
                })}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{settings.telegram?.enabled !== false ? 'مفعّل' : 'معطّل'}</span>
            </label>
          </div>
          <div className="form-group">
            <label>رابط القناة</label>
            <input
              type="url"
              value={settings.telegram.url}
              onChange={(e) => setSettings({
                ...settings,
                telegram: { ...settings.telegram, url: e.target.value }
              })}
              placeholder="https://t.me/username"
            />
          </div>
          <div className="form-group">
            <label>اسم المستخدم</label>
            <input
              type="text"
              value={settings.telegram.handle}
              onChange={(e) => setSettings({
                ...settings,
                telegram: { ...settings.telegram, handle: e.target.value }
              })}
              placeholder="@username"
            />
          </div>
          <div className="form-group">
            <label>الوصف</label>
            <textarea
              value={settings.telegram.description}
              onChange={(e) => setSettings({
                ...settings,
                telegram: { ...settings.telegram, description: e.target.value }
              })}
              placeholder="وصف القناة"
              rows="2"
            />
          </div>
        </div>
      </div>

      <div className="manager-footer">
        <button 
          className="save-button-large"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px', animation: 'spin 1s linear infinite' }}>
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
              </svg>
              جاري الحفظ...
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              حفظ جميع التعديلات
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ContactManager;
